// utils/BrandDataManager.js
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ErrorHandler, RetryHandler, CircuitBreaker } from './errorHandler';

class BrandDataManager {
  // In-memory cache
  static cache = {
    data: null,
    timestamp: null,
    duration: 5 * 60 * 1000, // 5 minutes
    version: '1.0'
  };

  // Circuit breaker for Firebase operations
  static circuitBreaker = new CircuitBreaker({
    failureThreshold: 3,
    resetTimeout: 30000, // 30 seconds
    monitoringPeriod: 10000 // 10 seconds
  });

  // localStorage keys
  static STORAGE_KEY = 'brandData';
  static STORAGE_TIMESTAMP_KEY = 'brandDataTimestamp';
  static STORAGE_VERSION_KEY = 'brandDataVersion';
  static STORAGE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Check if in-memory cache is valid
   */
  static isCacheValid() {
    if (!this.cache.data || !this.cache.timestamp) return false;
    const now = Date.now();
    return (now - this.cache.timestamp) < this.cache.duration;
  }

  /**
   * Check if localStorage cache is valid
   */
  static isLocalStorageValid() {
    try {
      const timestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);
      const version = localStorage.getItem(this.STORAGE_VERSION_KEY);
      
      if (!timestamp || version !== this.cache.version) return false;
      
      const now = Date.now();
      return (now - parseInt(timestamp)) < this.STORAGE_DURATION;
    } catch (error) {
      console.warn('Error checking localStorage validity:', error);
      return false;
    }
  }

  /**
   * Get brands from localStorage
   */
  static getFromLocalStorage() {
    try {
      if (!this.isLocalStorageValid()) return null;
      
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return null;
      
      const parsedData = JSON.parse(data);
      console.log('Loaded brands from localStorage:', parsedData.length);
      return parsedData;
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Save brands to localStorage
   */
  static saveToLocalStorage(data) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(this.STORAGE_VERSION_KEY, this.cache.version);
      console.log('Saved brands to localStorage:', data.length);
    } catch (error) {
      console.warn('Error saving to localStorage:', error);
      // Handle quota exceeded or other localStorage errors
      if (error.name === 'QuotaExceededError') {
        this.clearLocalStorage();
      }
    }
  }

  /**
   * Clear localStorage cache
   */
  static clearLocalStorage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
      localStorage.removeItem(this.STORAGE_VERSION_KEY);
      console.log('Cleared localStorage cache');
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
  }

  /**
   * Process and enhance brand data
   */
  static processBrandData(brands) {
    return brands.map(brand => {
      // Add computed fields for efficient filtering
      const rating = parseFloat(brand.rating) || 0;
      const isNew = brand.createdAt ? this.isNewBrand(brand.createdAt) : false;
      const isTopRated = rating >= 4.7;
      const hasBestBonus = brand.welcomeBonus && brand.welcomeBonus.includes('No Deposit');

      return {
        ...brand,
        rating,
        isNew,
        isTopRated,
        hasBestBonus,
        // Ensure slug exists for navigation
        slug: brand.slug || brand.id || 'brand'
      };
    });
  }

  /**
   * Check if brand is new (created within last 7 days)
   */
  static isNewBrand(createdAt) {
    try {
      const createdDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return createdDate > sevenDaysAgo;
    } catch (error) {
      return false;
    }
  }

  /**
   * Fetch brands from Firebase with comprehensive error handling
   */
  static async fetchFromFirebase() {
    const operation = async () => {
      console.log('Fetching brands from Firebase...');
      const startTime = performance.now();

      // Add timeout to prevent hanging requests (increased for more brands)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firebase request timeout')), 10000)
      );

      // Use simple collection query to get all brands
      const brandsRef = collection(db, "brands");
      
      // Get all brands without limit
      const querySnapshot = await Promise.race([
        getDocs(brandsRef),
        timeoutPromise
      ]);

      console.log('Firebase query completed, docs received:', querySnapshot.size);

      // Process all documents
      const allBrands = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allBrands.push({ id: doc.id, ...data });
      });

      // Process and sort on client side
      const processedBrands = this.processBrandData(allBrands);
      
      // Sort by rating and creation date
      processedBrands.sort((a, b) => {
        // First sort by rating (highest first)
        if (b.rating !== a.rating) {
          return b.rating - a.rating;
        }
        // Then by creation date (newest first)
        if (a.createdAt && b.createdAt) {
          const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        }
        return 0;
      });

      const endTime = performance.now();
      console.log(`Firebase fetch completed in ${(endTime - startTime).toFixed(2)}ms`);
      
      return processedBrands;
    };

    try {
      // Use circuit breaker to prevent cascading failures
      return await this.circuitBreaker.execute(operation);
    } catch (error) {
      // Handle error with comprehensive error handling
      const result = ErrorHandler.handleError(error, 'fetchFromFirebase', {
        retry: true,
        fallback: this.cache.data || this.getFromLocalStorage() || [],
        userMessage: 'Unable to load latest brands. Showing cached data.'
      });

      if (result.shouldRetry) {
        // Retry with exponential backoff
        const retryResult = await RetryHandler.withRetry(
          () => this.circuitBreaker.execute(operation),
          {
            maxRetries: 2,
            baseDelay: 1000,
            shouldRetry: (error) => {
              // Only retry on network/timeout errors
              return error.code === 'unavailable' || 
                     error.name === 'TimeoutError' ||
                     error.message?.includes('network');
            },
            onRetry: (error, attempt, delay) => {
              console.log(`Retrying Firebase fetch (attempt ${attempt}) in ${delay}ms`);
            }
          }
        );

        if (retryResult.success) {
          return retryResult.data;
        }
      }

      // If all retries failed, return cached data or throw error
      if (result.data && result.data.length > 0) {
        console.warn('Using cached data due to Firebase error');
        return result.data;
      }

      // No fallback data available, throw error
      console.error('No cached data available and Firebase connection failed');
      throw new Error('Unable to load brands. Please check your internet connection and try again.');
    }
  }

  /**
   * Get brands with intelligent caching and error handling
   */
  static async getBrands(options = {}) {
    const { forceRefresh = false, useCache = true } = options;

    try {
      // If force refresh is requested, skip cache
      if (forceRefresh) {
        const freshData = await this.fetchFromFirebase();
        this.updateCache(freshData);
        return freshData;
      }

      // Check in-memory cache first
      if (useCache && this.isCacheValid()) {
        console.log('Using in-memory cache');
        return this.cache.data;
      }

      // Check localStorage cache
      if (useCache) {
        const localData = this.getFromLocalStorage();
        if (localData) {
          // Update in-memory cache
          this.cache.data = localData;
          this.cache.timestamp = Date.now();
          console.log('Using localStorage cache');
          
          // Fetch fresh data in background if cache is getting stale
          const cacheAge = Date.now() - (parseInt(localStorage.getItem(this.STORAGE_TIMESTAMP_KEY)) || 0);
          if (cacheAge > this.cache.duration) {
            console.log('Cache is stale, refreshing in background');
            this.fetchFromFirebase()
              .then(freshData => this.updateCache(freshData))
              .catch(error => console.warn('Background refresh failed:', error));
          }
          
          return localData;
        }
      }

      // Fetch fresh data
      const freshData = await this.fetchFromFirebase();
      this.updateCache(freshData);
      return freshData;
    } catch (error) {
      // Comprehensive error handling with fallbacks
      const result = ErrorHandler.handleError(error, 'getBrands', {
        fallback: this.cache.data || this.getFromLocalStorage() || [],
        userMessage: 'Unable to load brands. Please check your connection and try again.'
      });

      if (result.data && result.data.length > 0) {
        console.warn('Using fallback data due to error:', error.message);
        return result.data;
      }

      // If no fallback data available, throw the error
      throw error;
    }
  }

  /**
   * Update both in-memory and localStorage cache
   */
  static updateCache(data) {
    // Update in-memory cache
    this.cache.data = data;
    this.cache.timestamp = Date.now();

    // Update localStorage
    this.saveToLocalStorage(data);
  }

  /**
   * Refresh cache in background
   */
  static async refreshCache() {
    try {
      console.log('Refreshing cache in background...');
      const freshData = await this.fetchFromFirebase();
      this.updateCache(freshData);
      return freshData;
    } catch (error) {
      console.warn('Background cache refresh failed:', error);
      return null;
    }
  }

  /**
   * Get cache status information
   */
  static getCacheStatus() {
    return {
      hasMemoryCache: !!this.cache.data,
      memoryCacheValid: this.isCacheValid(),
      memoryCacheAge: this.cache.timestamp ? Date.now() - this.cache.timestamp : null,
      hasLocalStorageCache: !!this.getFromLocalStorage(),
      localStorageCacheValid: this.isLocalStorageValid(),
      cacheSize: this.cache.data ? this.cache.data.length : 0,
      circuitBreakerState: this.circuitBreaker.getState(),
      errorStats: ErrorHandler.getErrorStats()
    };
  }
}

export default BrandDataManager;